import React, { useState, useMemo, useCallback } from 'react';
import { z } from 'zod';
import { clsx } from 'clsx';
import { UserRoundCog } from 'lucide-react';
import { useHasAdminAccess } from '../../hooks/useCurrentUserRole';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { usePersistentState } from '../../hooks/usePersistentState';
import { STORAGE_KEYS } from '../../utils/storage/storageKeyFactory';
import { useBackendVersion } from '../../hooks/useBackendVersion';

/**
 * 圖標組件 Props
 */
interface IconProps {
  className?: string;
  isActive?: boolean;
}

/**
 * 用戶圖標
 */
function UsersIcon({ className = 'w-5 h-5', isActive = false }: IconProps) {
  const strokeColor = isActive ? '#1e40af' : 'currentColor';
  const fillColor = isActive ? '#dbeafe' : 'none';
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
      <circle
        cx="9"
        cy="7"
        r="4"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
      <path
        d="M23 21v-2a4 4 0 0 0-3-3.87"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
      <path
        d="M16 3.13a4 4 0 0 1 0 7.75"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
    </svg>
  );
}

/**
 * 商品圖標
 */
function ProductsIcon({ className = 'w-5 h-5', isActive = false }: IconProps) {
  const strokeColor = isActive ? '#1e40af' : 'currentColor';
  const fillColor = isActive ? '#dbeafe' : 'none';
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="3"
        y="3"
        width="18"
        height="18"
        rx="2"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
      <path
        d="M9 3v18M15 3v18M3 9h18M3 15h18"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * 預購圖標
 */
function PreordersIcon({ className = 'w-5 h-5', isActive = false }: IconProps) {
  const strokeColor = isActive ? '#1e40af' : 'currentColor';
  const fillColor = isActive ? '#dbeafe' : 'none';
  const dotColor = isActive ? '#1e40af' : 'currentColor';
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="9"
        cy="21"
        r="1"
        stroke={dotColor}
        strokeWidth="1.5"
        fill={dotColor}
      />
      <circle
        cx="20"
        cy="21"
        r="1"
        stroke={dotColor}
        strokeWidth="1.5"
        fill={dotColor}
      />
      <path
        d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
    </svg>
  );
}

/**
 * 訂單圖標
 */
function OrdersIcon({ className = 'w-5 h-5', isActive = false }: IconProps) {
  const strokeColor = isActive ? '#1e40af' : 'currentColor';
  const fillColor = isActive ? '#dbeafe' : 'none';
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
      <path
        d="M14 2v6h6M16 13H8M16 17H8M10 9H8"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * 問卷圖標
 */
function SurveyIcon({ className = 'w-5 h-5', isActive = false }: IconProps) {
  const strokeColor = isActive ? '#1e40af' : 'currentColor';
  const fillColor = isActive ? '#dbeafe' : 'none';
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 19h7a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h3"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
      <path
        d="M9 9h6M9 13h4M9 17h2"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Chatbot 圖標
 */
function ChatbotIcon({ className = 'w-5 h-5', isActive = false }: IconProps) {
  const strokeColor = isActive ? '#1e40af' : 'currentColor';
  const fillColor = isActive ? '#dbeafe' : 'none';
  const dotColor = isActive ? '#1e40af' : 'currentColor';
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
      <circle cx="9" cy="10" r="1" fill={dotColor} />
      <circle cx="15" cy="10" r="1" fill={dotColor} />
      <path
        d="M8 14h8"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * 預約圖標
 */
function AppointmentsIcon({
  className = 'w-5 h-5',
  isActive = false,
}: IconProps) {
  const strokeColor = isActive ? '#1e40af' : 'currentColor';
  const fillColor = isActive ? '#dbeafe' : 'none';
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="3"
        y="4"
        width="18"
        height="18"
        rx="2"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
      <path
        d="M16 2v4M8 2v4M3 10h18"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 16h6"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * 出貨管理圖標
 */
function ShipmentsIcon({ className = 'w-5 h-5', isActive = false }: IconProps) {
  const strokeColor = isActive ? '#1e40af' : 'currentColor';
  const fillColor = isActive ? '#dbeafe' : 'none';
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
      <circle
        cx="5.5"
        cy="18.5"
        r="1.5"
        stroke={strokeColor}
        strokeWidth="1.5"
        fill={strokeColor}
      />
      <circle
        cx="18.5"
        cy="18.5"
        r="1.5"
        stroke={strokeColor}
        strokeWidth="1.5"
        fill={strokeColor}
      />
      <path
        d="M8 8h8M8 12h5"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * 推銷管理圖標
 */
function DiscountsIcon({ className = 'w-5 h-5', isActive = false }: IconProps) {
  const strokeColor = isActive ? '#1e40af' : 'currentColor';
  const fillColor = isActive ? '#dbeafe' : 'none';
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2L2 7l10 5 10-5-10-5z"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
      <path
        d="M2 17l10 5 10-5M2 12l10 5 10-5"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
      <path
        d="M7 9l5 3 5-3"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * 設定圖標
 */
function SettingsIcon({ className = 'w-5 h-5', isActive = false }: IconProps) {
  const strokeColor = isActive ? '#1e40af' : 'currentColor';
  const fillColor = isActive ? '#dbeafe' : 'none';
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="12"
        cy="12"
        r="3"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
      <path
        d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * 眼睛圖標（張開）
 */
function EyeOpenIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * 眼睛圖標（閉著）
 */
function EyeClosedIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1 1l22 22"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * 導航項目類型
 */
interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
}

/**
 * 基礎導航項目配置
 * 根據 Feature Flags 動態顯示
 */
const getBaseNavItems = (
  currentRoute: string,
  isEnabled: (key: string) => boolean
): NavItem[] => {
  const items: NavItem[] = [
    {
      id: 'users',
      label: '用戶管理',
      icon: <UsersIcon isActive={currentRoute === 'users'} />,
    },
    {
      id: 'products',
      label: '商品管理',
      icon: <ProductsIcon isActive={currentRoute === 'products'} />,
    },
  ];

  // 預購功能（orders:preorder）
  if (isEnabled('orders:preorder')) {
    items.push({
      id: 'preorders',
      label: '面交預購管理',
      icon: <PreordersIcon isActive={currentRoute === 'preorders'} />,
    });
  }

  // Coupon feature hidden - 優惠券功能已隱藏 (2024-11-11) - May be restored in the future
  items.push({
    id: 'orders',
    label: '訂單管理',
    icon: <OrdersIcon isActive={currentRoute === 'orders'} />,
  });

  // 出貨管理（orders:shipment）
  if (isEnabled('orders:shipment')) {
    items.push({
      id: 'shipments',
      label: '出貨管理',
      icon: <ShipmentsIcon isActive={currentRoute === 'shipments'} />,
    });
  }

  // 預約管理（module:appointment）
  if (isEnabled('module:appointment')) {
    items.push({
      id: 'appointments',
      label: '預約管理',
      icon: <AppointmentsIcon isActive={currentRoute === 'appointments'} />,
    });
  }

  items.push({
    id: 'marketing',
    label: '行銷管理',
    icon: <DiscountsIcon isActive={currentRoute === 'marketing'} />,
  });
  items.push({
    id: 'survey-design',
    label: '問卷設計',
    icon: <SurveyIcon isActive={currentRoute === 'survey-design'} />,
  });

  // RAG Chatbot（ai:rag_chatbot）
  if (isEnabled('ai:rag_chatbot')) {
    items.push({
      id: 'rag-chatbot',
      label: 'RAG Chatbot',
      icon: <ChatbotIcon isActive={currentRoute === 'rag-chatbot'} />,
    });
  }

  return items;
};

/**
 * Admin 側邊欄元件屬性
 */
interface AdminSidebarProps {
  currentRoute: string;
  onRouteChange: (route: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

/**
 * Admin 側邊欄元件
 */
export function AdminSidebar({
  currentRoute,
  onRouteChange,
  isCollapsed,
  onToggleCollapse,
}: AdminSidebarProps) {
  const { role } = useHasAdminAccess();
  // 自訂模式狀態
  const [isCustomizeMode, setIsCustomizeMode] = useState(false);

  // 導航項目顯示狀態（持久化，使用 global scope）
  const [navItemVisibility, setNavItemVisibility] = usePersistentState(
    STORAGE_KEYS.ADMIN_NAV_VISIBILITY(),
    {} as Record<string, boolean>,
    z.record(z.string(), z.boolean()),
    { scope: 'global' }
  );

  // 取得 Feature Flags
  // 直接從 localStorage 讀取，useFeatureFlags 內部會使用 useMemo 穩定值
  const tenantId =
    typeof window !== 'undefined' ? localStorage.getItem('tenant_id') : null;

  const { isLoading, error, flags } = useFeatureFlags(tenantId || undefined);

  // 使用 useMemo 計算 Feature Flags 狀態，避免在每次渲染時重新計算
  const featureFlagsStatus = useMemo(() => {
    if (isLoading || error || !flags.length) {
      return {
        'orders:preorder': false,
        'orders:shipment': false,
        'module:appointment': false,
        'ai:rag_chatbot': false,
        'crm:loyalty_program': false,
      };
    }
    return {
      'orders:preorder': flags.includes('orders:preorder'),
      'orders:shipment': flags.includes('orders:shipment'),
      'module:appointment': flags.includes('module:appointment'),
      'ai:rag_chatbot': flags.includes('ai:rag_chatbot'),
      'crm:loyalty_program': flags.includes('crm:loyalty_program'),
    };
  }, [isLoading, error, flags]);

  // 動態構建導航項目
  // 根據 Feature Flags 過濾功能
  // 注意：如果 Feature Flags 還在載入中，先顯示基本項目（避免閃爍）
  // 使用 useMemo 穩定 baseNavItems，避免不必要的重新計算
  // 創建一個穩定的 isEnabled 檢查函數，基於 featureFlagsStatus
  const checkFeatureEnabled = useCallback(
    (key: string): boolean => {
      return (
        featureFlagsStatus[key as keyof typeof featureFlagsStatus] ?? false
      );
    },
    [featureFlagsStatus]
  );

  const baseNavItems = useMemo(
    () => getBaseNavItems(currentRoute, checkFeatureEnabled),
    [currentRoute, checkFeatureEnabled]
  );

  // 使用 useMemo 穩定 allNavItems，避免不必要的重新計算
  const allNavItems: NavItem[] = useMemo(
    () => [
      ...baseNavItems,
      // 設定相關項目放在最下面
      // 可預約時段設定（module:appointment）
      ...(checkFeatureEnabled('module:appointment')
        ? [
            {
              id: 'availability-settings',
              label: '可預約時段設定',
              icon: (
                <SettingsIcon
                  isActive={currentRoute === 'availability-settings'}
                />
              ),
            },
          ]
        : []),
      {
        id: 'staff-management',
        label: '員工管理',
        icon: (
          <UserRoundCog
            className={clsx(
              'w-5 h-5 shrink-0',
              currentRoute === 'staff-management'
                ? 'text-blue-100'
                : 'text-white/90'
            )}
          />
        ),
      },
      // 設定僅 OWNER 可見，管理者不可見
      ...(role === 'OWNER'
        ? [
            {
              id: 'tenant-settings',
              label: '設定',
              icon: (
                <SettingsIcon isActive={currentRoute === 'tenant-settings'} />
              ),
            },
          ]
        : []),
    ],
    [baseNavItems, currentRoute, checkFeatureEnabled, role]
  );

  const backendVersion = useBackendVersion();

  return (
    <div
      className={clsx(
        'fixed left-0 top-16 h-[calc(100vh-4rem)] bg-gradient-to-b from-blue-600 to-blue-700 shadow-xl z-40 transition-all duration-300 flex flex-col',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* 標題區域 */}
      <div
        className={clsx(
          'p-4 border-b border-blue-500/30 flex items-center relative flex-shrink-0',
          isCollapsed ? 'justify-center' : ''
        )}
      >
        {/* 折疊按鈕 - 始終顯示在最左側 */}
        <button
          onClick={onToggleCollapse}
          className={clsx(
            'p-2 rounded-lg hover:bg-blue-500/50 transition-all duration-200 ease-out active:opacity-70 min-w-[44px] min-h-[44px] flex items-center justify-center bg-blue-500/20 border border-blue-400/40 hover:bg-blue-500/60 hover:border-blue-300/60',
            isCollapsed ? '' : 'mr-2'
          )}
          title={isCollapsed ? '展開側邊欄' : '收起側邊欄'}
          aria-label={isCollapsed ? '展開側邊欄' : '收起側邊欄'}
        >
          <svg
            className={clsx(
              'w-5 h-5 text-white transition-transform duration-300',
              isCollapsed ? 'rotate-180' : ''
            )}
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
            />
          </svg>
        </button>

        {!isCollapsed && (
          <div className="flex items-center justify-between flex-1">
            <div>
              <p className="text-base text-blue-100 font-semibold">管理後台</p>
            </div>
            <button
              onClick={() => setIsCustomizeMode(!isCustomizeMode)}
              className={clsx(
                'px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200',
                isCustomizeMode
                  ? 'bg-white text-blue-700 hover:bg-blue-50'
                  : 'bg-blue-500/30 text-blue-100 hover:bg-blue-500/50 border border-blue-400/40'
              )}
              title={isCustomizeMode ? '完成自訂' : '自訂導航'}
            >
              {isCustomizeMode ? '完成' : '自訂'}
            </button>
          </div>
        )}
      </div>

      {/* 導航選單（可捲動區域） */}
      <nav className="flex-1 overflow-y-auto p-4">
        <ul className="space-y-2 pb-20">
          {allNavItems.map(item => {
            const isVisible = navItemVisibility[item.id] !== false;
            // 在自訂模式下顯示所有項目，否則只顯示可見的項目
            const shouldShow = isCustomizeMode || isVisible;

            if (!shouldShow) return null;

            return (
              <li key={item.id}>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => !isCustomizeMode && onRouteChange(item.id)}
                    disabled={isCustomizeMode}
                    className={clsx(
                      'flex-1 flex items-center px-4 py-3 text-left rounded-xl transition-all duration-200 ease-out group active:opacity-70 min-h-[44px]',
                      currentRoute === item.id
                        ? 'bg-white text-blue-700 shadow-lg shadow-blue-500/20'
                        : 'text-blue-50 hover:bg-blue-500/50 hover:text-white',
                      isCollapsed ? 'justify-center' : '',
                      isCustomizeMode && 'cursor-default'
                    )}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <span className="flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </span>
                    {!isCollapsed && (
                      <span className="font-semibold ml-3">{item.label}</span>
                    )}
                  </button>
                  {isCustomizeMode && !isCollapsed && (
                    <button
                      onClick={() => {
                        setNavItemVisibility(prev => ({
                          ...prev,
                          [item.id]: !isVisible,
                        }));
                      }}
                      className={clsx(
                        'p-2 rounded-lg transition-all duration-200 hover:bg-blue-500/50 flex-shrink-0',
                        isVisible ? 'text-blue-100' : 'text-blue-300'
                      )}
                      title={isVisible ? '點擊隱藏' : '點擊顯示'}
                    >
                      {isVisible ? (
                        <EyeOpenIcon className="w-5 h-5" />
                      ) : (
                        <EyeClosedIcon className="w-5 h-5" />
                      )}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* 底部資訊 */}
      {!isCollapsed && (
        <div className="p-4 border-t border-blue-500/30 flex-shrink-0">
          <div className="text-xs text-blue-100 text-center space-y-0.5">
            <p>
              Emvyax 版本{' '}
              {backendVersion?.version ? `@${backendVersion.version}` : '@2026'}
            </p>
            <p className="mt-1">© 2026</p>
          </div>
        </div>
      )}
    </div>
  );
}
